import { Component, DestroyRef, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { FormFieldComponent } from '../../shared/components/form-field/form-field.component';
import { ButtonPadraoComponent } from '../../shared/components/button-padrao/button-padrao.component';
import { ModalConfirmacaoComponent } from '../../shared/modais/modal-confirmacao/modal-confirmacao.component';
import { MsgService } from '../../shared/service/msg.service';
import { PlataformaService } from '../../shared/service/plataforma.service';
import { PlataformaCardModel } from '../../shared/model/plataforma-card-model';

interface PlataformaCardView {
  chave: number;
  id?: number;
  vinculoId?: number;
  nome: string;
  urlImg?: string;
  urlBaseDinamica: string;
  urlDadosBaseDinamica: string;
  token: string;
  novo: boolean;
  salvando: boolean;
}

@Component({
  selector: 'app-card-plataforma',
  standalone: true,
  imports: [
    FormFieldComponent,
    ButtonPadraoComponent
  ],
  templateUrl: './card-plataforma.component.html',
  styleUrl: './card-plataforma.component.css'
})
export class CardPlataformaComponent implements OnInit {

  private readonly plataformaService = inject(PlataformaService);
  private readonly msgService = inject(MsgService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('inputLogo') inputLogo!: ElementRef<HTMLInputElement>;

  plataformas: PlataformaCardView[] = [];
  carregando = true;
  skeletonItens = [1, 2];

  private contadorChave = 0;
  private cardLogoAlvo?: PlataformaCardView;

  ngOnInit() {
    this.carregarPlataformas();
  }

  carregarPlataformas() {
    this.carregando = true;
    this.plataformaService.buscarCards().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        this.plataformas = (res ?? []).map(p => this.paraView(p));
        this.carregando = false;
      },
      error: () => {
        this.carregando = false;
        this.msgService.msgErro('Erro ao buscar as plataformas');
      }
    });
  }

  adicionarPlataforma() {
    this.plataformas.push({
      chave: this.contadorChave++,
      nome: '',
      urlBaseDinamica: '',
      urlDadosBaseDinamica: '',
      token: '',
      novo: true,
      salvando: false
    });
  }

  motivoBloqueio(card: PlataformaCardView): string | null {
    if (!card.nome?.trim()) return 'Informe o nome da plataforma.';
    if (!this.urlValida(card.urlBaseDinamica)) return 'Informe uma URL de busca da base dinâmica válida.';
    if (!this.urlValida(card.urlDadosBaseDinamica)) return 'Informe uma URL dos dados da base dinâmica válida.';
    if (!card.token?.trim()) return 'Informe o token da plataforma.';
    return null;
  }

  podeSalvar(card: PlataformaCardView): boolean {
    return this.motivoBloqueio(card) === null && !card.salvando;
  }

  salvar(card: PlataformaCardView) {
    const motivo = this.motivoBloqueio(card);
    if (motivo) {
      this.msgService.msgAdvertencia(motivo);
      return;
    }
    if (card.salvando) {
      return;
    }

    const dto: PlataformaCardModel = {
      id: card.id,
      nome: card.nome.trim(),
      urlBaseDinamica: card.urlBaseDinamica.trim(),
      urlDadosBaseDinamica: card.urlDadosBaseDinamica.trim(),
      token: card.token.trim()
    };

    card.salvando = true;
    this.plataformaService.salvarCard(dto).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        card.id = res.id;
        card.vinculoId = res.vinculoId;
        card.urlImg = res.urlImg;
        card.novo = false;
        this.msgService.msgSucesso('Plataforma salva com sucesso!');
      },
      error: (err) => this.msgService.msgErro(err?.error?.message ?? 'Erro ao salvar a plataforma'),
      complete: () => card.salvando = false
    });
  }

  inativar(card: PlataformaCardView, index: number) {
    if (card.novo || !card.id) {
      this.plataformas.splice(index, 1);
      return;
    }

    const dialogRef = this.dialog.open(ModalConfirmacaoComponent, {
      data: {
        texto: 'Tem certeza que deseja inativar esta plataforma?',
        subTexto: 'Ela deixará de aparecer para o seu grupo.'
      }
    });

    dialogRef.afterClosed().subscribe(confirmado => {
      if (confirmado) {
        this.confirmarInativacao(card, index);
      }
    });
  }

  private confirmarInativacao(card: PlataformaCardView, index: number) {
    this.plataformaService.inativarCard(card.id!).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.plataformas.splice(index, 1);
        this.msgService.msgSucesso('Plataforma inativada com sucesso');
      },
      error: (err) => this.msgService.msgErro(err?.error?.message ?? 'Erro ao inativar a plataforma')
    });
  }

  abrirSeletorLogo(card: PlataformaCardView) {
    if (!card.id) {
      this.msgService.msgAdvertencia('Salve a plataforma antes de adicionar a logo.');
      return;
    }
    this.cardLogoAlvo = card;
    this.inputLogo.nativeElement.value = '';
    this.inputLogo.nativeElement.click();
  }

  aoSelecionarLogo(event: Event) {
    const input = event.target as HTMLInputElement;
    const arquivo = input.files?.[0];
    if (!arquivo || !this.cardLogoAlvo?.id) {
      return;
    }
    const card = this.cardLogoAlvo;
    this.plataformaService.atualizarLogo(card.id!, arquivo).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        card.urlImg = res.urlImg;
        this.msgService.msgSucesso('Logo atualizada com sucesso');
      },
      error: (err) => this.msgService.msgErro(err?.error?.message ?? 'Erro ao atualizar a logo')
    });
  }

  private paraView(p: PlataformaCardModel): PlataformaCardView {
    return {
      chave: this.contadorChave++,
      id: p.id,
      vinculoId: p.vinculoId,
      nome: p.nome ?? '',
      urlImg: p.urlImg,
      urlBaseDinamica: p.urlBaseDinamica ?? '',
      urlDadosBaseDinamica: p.urlDadosBaseDinamica ?? '',
      token: p.token ?? '',
      novo: false,
      salvando: false
    };
  }

  private urlValida(valor?: string): boolean {
    const v = (valor ?? '').trim();
    if (!v) return false;
    try {
      const u = new URL(v);
      return u.protocol === 'http:' || u.protocol === 'https:';
    } catch {
      return false;
    }
  }
}
